import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { OutcomesForParty } from "../../shared/Analytics";
import { chanceOfPositive, ShownOutcome } from "../../shared/adjustedView";
import { ZERO } from "../../shared/bigUtil";
import { percentage } from "../../shared/percentage";
import { Stage } from "../../shared/CaseConfiguration";
import { Metric } from "../general/dataAtoms/Metric";
import { SectionLabel } from "../general/SectionLabel";
import {
  groupJudgmentsByNetResult,
  JudgmentsWithSameNetResult,
} from "./CaseOutcomeUtility";
import { ChanceArrow } from "./VsLitigation";
import { OutcomeGroup, OutcomesByNetResult } from "./OutcomesByNetResult";
import { useLitigation } from "./ShownResults";
import { useOutcomeDialog } from "./OutcomeDialog";

export const CaseOutcome = ({
  outcome,
  partyName,
}: {
  outcome: OutcomesForParty<ShownOutcome>;
  partyName: string;
}) => {
  const judgmentsByNetResult: JudgmentsWithSameNetResult[] = useMemo(
    () => groupJudgmentsByNetResult(outcome),
    [outcome],
  );
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.outcomes",
  });
  const outcomeDialog = useOutcomeDialog(outcome, partyName);
  const caseContext = useCase();
  const { t: tShown } = useTranslation(undefined, {
    keyPrefix: "case.shownResults",
  });
  // in the settlement-adjusted view, the party's outcomes as litigated
  const litigation = useLitigation();
  const adjustedView = litigation !== caseContext;
  const litigatedOutcomes = litigation
    .getOutcomes()
    .find(({ partyId }) => partyId === outcome.partyId)?.outcomes;
  const issues = caseContext.getIssueDescriptors();
  const parties = caseContext.getPartyDescriptors();
  const claimMatrix = caseContext.getClaimMatrix();
  const c = caseContext.getCase();

  // what the chart and the table draw of each group
  const groups: OutcomeGroup[] = useMemo(
    () =>
      judgmentsByNetResult.map(
        ({ netResult, judgments, settlementStages }) => ({
          netResult,
          settlementStages,
          probability: judgments.reduce(
            (sum, judgement) => sum + judgement.probability,
            0,
          ),
          outcomes: judgments.length,
        }),
      ),
    [judgmentsByNetResult],
  );

  if (judgmentsByNetResult.length === 0)
    return (
      <Typography key="stage-label">
        {t("description", { count: 0 })}
      </Typography>
    );

  let negativeNetOutcomesProbability = ZERO;
  let positiveJudgments = 0;
  let positiveNetOutcomes = 0;
  judgmentsByNetResult.forEach((judgmentGroup) => {
    judgmentGroup.judgments.forEach(({ judgementValue, probability }) => {
      if (judgementValue > 0) positiveJudgments++;
      if (judgmentGroup.netResult < 0)
        negativeNetOutcomesProbability =
          negativeNetOutcomesProbability.add(probability);
      else if (judgmentGroup.netResult > 0) positiveNetOutcomes++;
    });
  });
  // more than nothing, to the cent, as the net results page has it
  const chanceOf = (
    outcomes: { value: number; netResult: number; probability: number }[],
    of: "value" | "netResult",
  ) =>
    outcomes
      .filter((o) => o[of] >= 0.005)
      .reduce((sum, { probability }) => sum + probability, 0);
  const positiveJudgment = chanceOf(outcome.outcomes, "value");
  const positiveNet = chanceOfPositive(outcome.outcomes);
  // in the settlement-adjusted view, the same of the case as litigated
  const litigated = adjustedView ? litigatedOutcomes : undefined;
  const inLitigation = (chance: number | undefined) =>
    chance === undefined
      ? "\u00a0"
      : tShown("inLitigation", { chance: percentage.format(chance) });

  const allOutcomesNegative = groups.every(({ netResult }) => netResult < 0);

  // the net results of the other view, which the chart keeps on its scale:
  // as litigated in the settlement-adjusted view, with settlements in the
  // litigation view
  const ofAdjusted =
    c.settlementAdjusted?.status === "done"
      ? c.settlementAdjusted.parties.find(
          ({ partyId }) => partyId === outcome.partyId,
        )
      : undefined;
  const otherNetResults = adjustedView
    ? (litigatedOutcomes ?? []).map(({ netResult }) => netResult)
    : [
        ...(ofAdjusted?.outcomes ?? []),
        ...(ofAdjusted?.settlement?.points ?? []),
      ].map(({ netResult }) => netResult);

  // every figure has a line under it in both views, so switching moves nothing
  const metrics: {
    label: string;
    value: string;
    caption: string;
    after?: ReactNode;
  }[] = [
    {
      label: t("summary.judgments.total"),
      value: String(outcome.outcomes.length),
      caption: "\u00a0",
    },
    {
      label: t("summary.judgments.positive"),
      value: String(positiveJudgments),
      caption: "\u00a0",
    },
    {
      label: t("summary.judgments.positiveProbability"),
      value: percentage.format(positiveJudgment),
      caption: inLitigation(litigated && chanceOf(litigated, "value")),
      after: litigated && (
        <ChanceArrow
          chance={positiveJudgment}
          without={chanceOf(litigated, "value")}
          of={t("summary.judgments.positiveOf")}
        />
      ),
    },
    {
      label: t("summary.outcomes.total"),
      value: String(judgmentsByNetResult.length),
      caption: "\u00a0",
    },
    {
      label: t("summary.outcomes.positive"),
      value: String(positiveNetOutcomes),
      caption: "\u00a0",
    },
    {
      label: t("summary.outcomes.positiveProbability"),
      value: percentage.format(positiveNet),
      caption: inLitigation(litigated && chanceOfPositive(litigated)),
      after: litigated && (
        <ChanceArrow
          chance={positiveNet}
          without={chanceOfPositive(litigated)}
          of={t("summary.outcomes.positiveOf")}
        />
      ),
    },
  ];

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.5 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box>
            <SectionLabel component="h2">{t("summary.title")}</SectionLabel>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              {t("description", { name: partyName })}
            </Typography>
          </Box>
          {allOutcomesNegative ? (
            <Alert severity="error" sx={{ py: 0 }}>
              {t("summary.alerts.allNegative", { name: partyName })}
            </Alert>
          ) : (
            negativeNetOutcomesProbability.gt(0.5) && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {t("summary.alerts.mostNegative", {
                  name: partyName,
                  probability: percentage.format(
                    negativeNetOutcomesProbability.toNumber(),
                  ),
                })}
              </Alert>
            )
          )}
        </Box>
        <Box
          sx={{
            // as wide as each figure needs, wrapping where the row is full
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
            "& > *": { flex: "1 1 220px", minWidth: "max-content" },
          }}
        >
          {metrics.map(({ label, value, caption, after }) => (
            <Metric
              key={label}
              label={label}
              value={value}
              caption={caption}
              after={after}
            />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <SectionLabel component="h2">
            {t("chart.netResults.title")}
          </SectionLabel>
          <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
            {t("judgments.description")}
          </Typography>
        </Box>
        <OutcomesByNetResult
          groups={groups}
          extent={otherNetResults}
          litigationChanceOf={
            litigated &&
            ((netResult) =>
              litigated
                .filter((o) => Math.abs(o.netResult - netResult) < 0.005)
                .reduce((sum, { probability }) => sum + probability, 0))
          }
          onOpen={(index) => outcomeDialog.open(judgmentsByNetResult[index])}
          onOpenStage={(stage) => {
            const group = stageGroupOf(judgmentsByNetResult, stage);
            if (group) outcomeDialog.open(group);
          }}
        />
      </Paper>
      {outcomeDialog.dialog}
    </Stack>
  );
};

/** The settled outcomes of one stage, as a group of their own to open. */
function stageGroupOf(
  groups: JudgmentsWithSameNetResult[],
  stage: Stage,
): JudgmentsWithSameNetResult | undefined {
  const settlement = groups.find(({ settlementStages }) => settlementStages);
  const row = settlement?.settlementStages?.find((s) => s.stage === stage);
  return settlement && row
    ? {
        netResult: row.netResult,
        judgments: settlement.judgments.filter((j) => j.settledAt === stage),
      }
    : undefined;
}
